/**
 * Shared auth schemas.
 *
 * `AuthUser` is the canonical shape of an authenticated principal as seen by
 * the API and the frontend after Firebase JWT verification + user-bootstrap.
 */
import { z } from "zod";

/**
 * The minimum identity we trust from a verified Firebase ID token,
 * plus the DB-side user id assigned at bootstrap time.
 *
 * Note: callers must not include the raw ID token here — this struct is
 * safe to log (no PII beyond email/displayName, both already in our DB).
 */
export const AuthUserSchema = z.object({
  /** Internal user id (uuid) — assigned by the API on first sign-in. */
  id: z.string().uuid(),
  /** Firebase Authentication uid (sub claim). */
  firebaseUid: z.string().min(1),
  /** Verified email address. */
  email: z.string().email(),
  /** Display name from the Firebase profile, if any. */
  displayName: z.string().nullable(),
  /** Personal/primary org id for this user. */
  orgId: z.string().uuid(),
});

export type AuthUser = z.infer<typeof AuthUserSchema>;
